// ============================================================================
// src/app/externalSources/externalSourceRegistry.ts
// ----------------------------------------------------------------------------
// Shared ownership for non-Supabase, external HTTP sources.
//
// This deliberately does NOT use routeDataCatalog or the authenticated route
// resource runtime. Those modules describe subject-scoped Supabase reads and
// their resource-to-surface receipts. An external public endpoint has a
// different identity, cache lifetime, and cancellation policy, so it gets an
// explicit owner instead of being disguised as an empty route resource.
//
// A source keeps one request alive independently of its React subscribers.
// Unsubscribing only stops notifications for that subscriber; it never aborts
// the request. That matters for app-wide chrome and a route page sharing one
// public request: navigating away from either must not cancel the other.
// ============================================================================

export type ExternalSourceStatus = 'loading' | 'ready' | 'unavailable';

export type ExternalSourceSnapshot<Data> = Readonly<{
  data: Data | null;
  status: ExternalSourceStatus;
  fetching: boolean;
  error: unknown | null;
  updatedAt: number | null;
}>;

export type ExternalSourceLoadOptions = Readonly<{
  force?: boolean;
}>;

export type ExternalSourceLoader<Data> = (options: Required<ExternalSourceLoadOptions>) => Promise<Data | null>;

export type ExternalSourceRegistryOptions<Data> = Readonly<{
  cacheMs: number;
  load: ExternalSourceLoader<Data>;
}>;

export type ExternalSourceRegistry<Data> = Readonly<{
  getSnapshot: () => ExternalSourceSnapshot<Data>;
  subscribe: (listener: () => void) => () => void;
  load: (options?: ExternalSourceLoadOptions) => Promise<Data | null>;
}>;

function initialSnapshot<Data>(): ExternalSourceSnapshot<Data> {
  // The old weather consumers both start with a loading frame. Keeping that
  // state before the first subscription preserves their first paint while the
  // registry remains inert until somebody actually subscribes.
  return {data: null, status: 'loading', fetching: false, error: null, updatedAt: null};
}

/**
 * Creates one process-local owner for a public/external source.
 *
 * `load` intentionally receives only `{force}`. There is no AbortSignal in
 * this contract and no AbortController in the registry: the shared request is
 * owned by the source, not by whichever component happened to subscribe first.
 */
export function createExternalSourceRegistry<Data>({
  cacheMs,
  load: loadFromNetwork,
}: ExternalSourceRegistryOptions<Data>): ExternalSourceRegistry<Data> {
  let snapshot = initialSnapshot<Data>();
  let freshAt = 0;
  let inFlight: Promise<Data | null> | null = null;
  const listeners = new Set<() => void>();

  const publish = (next: ExternalSourceSnapshot<Data>) => {
    snapshot = next;
    for (const listener of Array.from(listeners)) listener();
  };

  const load = ({force = false}: ExternalSourceLoadOptions = {}): Promise<Data | null> => {
    // A non-forced caller receives the same 15-minute reading without starting
    // a request or publishing a redundant state transition.
    if (!force && snapshot.data !== null && Date.now() - freshAt < cacheMs) {
      return Promise.resolve(snapshot.data);
    }

    // This applies to a forced refresh too. It preserves the existing weather
    // contract: an explicit refresh arriving while any forecast request is in
    // flight joins that request instead of creating a competing fetch.
    if (inFlight) return inFlight;

    const previous = snapshot.data;
    publish({
      data: previous,
      // A refresh leaves a usable reading visible. Only a first request paints
      // the loading state.
      status: previous === null ? 'loading' : 'ready',
      fetching: true,
      error: null,
      updatedAt: snapshot.updatedAt,
    });

    const request = Promise.resolve()
      .then(() => loadFromNetwork({force}))
      .then((data) => {
        if (data !== null) {
          const updatedAt = Date.now();
          freshAt = updatedAt;
          publish({data, status: 'ready', fetching: false, error: null, updatedAt});
          return data;
        }

        // A failed forced refresh must retain the last truthful reading. The
        // Weather page already does this locally; keeping it at the owner makes
        // the header and page agree without blanking either surface.
        publish({
          data: previous,
          status: previous === null ? 'unavailable' : 'ready',
          fetching: false,
          error: null,
          updatedAt: snapshot.updatedAt,
        });
        return null;
      })
      .catch((error: unknown) => {
        publish({
          data: previous,
          status: previous === null ? 'unavailable' : 'ready',
          fetching: false,
          error,
          updatedAt: snapshot.updatedAt,
        });
        return null;
      });

    inFlight = request;
    void request.finally(() => {
      if (inFlight === request) inFlight = null;
    });
    return request;
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    // Starting the request after registering the listener means the first
    // subscriber observes its loading state. The promise cannot reject because
    // load() records failures in the snapshot, so this fire-and-forget call is
    // safe and never becomes an unhandled rejection.
    void load();
    return () => {
      listeners.delete(listener);
      // Deliberately no cancellation here. A second subscriber, or a later
      // remount, is entitled to the same in-flight public request.
    };
  };

  return Object.freeze({
    getSnapshot: () => snapshot,
    subscribe,
    load,
  });
}
